import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChartSimple,
  faUserGroup,
  faFingerprint,
  faHandHoldingDollar,
  faSitemap,
  faGears,
  faUser,
  faPersonCircleCheck,
  faChartColumn,
  faBusinessTime,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarPlus, faComments, faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import type { RouteLocationRaw } from 'vue-router';
import { PlatformModule } from '@/utils/enums';
import { ALL_PLATFORM_MODULE_KEYS } from '@/utils/modules';
import {
  DASHBOARD_ROUTE,
  REGISTER_EMPLOYEE_ROUTE,
  EMPLOYEES_ROUTE,
  EMPLOYEE_GENERAL_ROUTE,
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEE_PAYROLL_SALARY_ROUTE,
  EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
  EMPLOYEE_PROMOTION_HISTORY_ROUTE,
  EMPLOYEE_ALLOWANCE_ROUTE,
  EMPLOYEE_LEAVE_ROUTE,
  EMPLOYEE_OVERTIME_ROUTE,
  EMPLOYEE_ATTENDANCE_ROUTE,
  EMPLOYEE_CONTRACTS_ROUTE,
  EMPLOYEE_DOCUMENT_ROUTE,
  EMPLOYEE_SETTINGS_ROUTE,
  EMPLOYEE_ACTIVITY_ROUTE,
  EMPLOYEE_OFFBOARDING_ROUTE,
  PAYROLL_ROUTE,
  PAYROLL_SUMMARY_ROUTE,
  PAYROLL_DETAILS_ROUTE,
  PAYROLL_SETTINGS_ROUTE,
  LEAVES_ROUTE,
  HOLIDAYS_ROUTE,
  LEAVE_TYPES_ROUTE,
  ATTENDANCE_ROUTE,
  ATTENDANCE_SETTING_ROUTE,
  OVERTIME_ROUTE,
  ORGANIZATION_STRUCTURE_ROUTE,
  RECRUITMENT_ROUTE,
  ADD_JOB_POST_ROUTE,
  EDIT_JOB_POST_ROUTE,
  JOB_POST_DETAILS_ROUTE,
  JOB_APPLICATIONS_ROUTE,
  PROFILE_GENERAL_ROUTE,
  PROFILE_PAYROLL_ROUTE,
  PROFILE_ALLOWANCE_ROUTE,
  PROFILE_LEAVE_ROUTE,
  PROFILE_OVERTIME_ROUTE,
  PROFILE_ATTENDANCE_ROUTE,
  PROJECTS_ROUTE,
  PROJECT_DETAILS_ROUTE,
  REPORTS_ROUTE,
  REPORT_BUILDER_ROUTE,
  GENERAL_SETTINGS_ROUTE,
  SECURITY_SETTINGS_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  OFFBOARDING_SETTINGS_ROUTE,
  EXPORT_TEMPLATES_ROUTE,
  CREATE_EXPORT_TEMPLATE_ROUTE,
  EDIT_EXPORT_TEMPLATE_ROUTE,
  CHAT_ROUTE,
  CHAT_CONVERSATION_ROUTE,
  FILES_ROUTE,
  FILES_FOLDER_ROUTE,
} from '@/router/routes';

library.add(faChartSimple, faUserGroup, faFingerprint, faHandHoldingDollar, faSitemap, faGears, faUser, faPersonCircleCheck, faChartColumn, faBusinessTime, faCalendarPlus, faListCheck, faComments, faFolderOpen);

/** A destination in the main navigation (desktop sidebar and mobile bottom bar). */
export interface NavItem {
  key: string;
  label: string;
  /** Label for tight spaces such as the bottom bar. */
  shortLabel: string;
  icon: string;
  to: RouteLocationRaw;
  active: boolean;
  /** Keeps the item's tab in the mobile bottom bar when the rest move into "More". */
  bottomBar: boolean;
  /** Module the destination belongs to; core destinations have none. */
  module: PlatformModule | null;
  /** Unread count shown next to the label (chat); 0 hides it. */
  badge: number;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

interface NavContext {
  isAdmin: boolean;
  accountId?: number | string;
  routeName?: unknown;
  /** Modules the organization is licensed for; defaults to all of them. */
  modules?: PlatformModule[];
  /** Unread chat messages, shown as a badge on the Chat item. */
  chatUnread?: number;
}

interface ItemOptions {
  shortLabel?: string;
  bottomBar?: boolean;
  module?: PlatformModule | null;
  badge?: number;
}

const EMPLOYEE_ROUTES = [
  REGISTER_EMPLOYEE_ROUTE,
  EMPLOYEES_ROUTE,
  EMPLOYEE_GENERAL_ROUTE,
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEE_PAYROLL_SALARY_ROUTE,
  EMPLOYEE_PROMOTION_HISTORY_ROUTE,
  EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
  EMPLOYEE_ALLOWANCE_ROUTE,
  EMPLOYEE_LEAVE_ROUTE,
  EMPLOYEE_OVERTIME_ROUTE,
  EMPLOYEE_ATTENDANCE_ROUTE,
  EMPLOYEE_CONTRACTS_ROUTE,
  EMPLOYEE_DOCUMENT_ROUTE,
  EMPLOYEE_SETTINGS_ROUTE,
  EMPLOYEE_ACTIVITY_ROUTE,
  EMPLOYEE_OFFBOARDING_ROUTE,
];
const PROFILE_ROUTES = [PROFILE_GENERAL_ROUTE, PROFILE_PAYROLL_ROUTE, PROFILE_ALLOWANCE_ROUTE, PROFILE_OVERTIME_ROUTE, PROFILE_ATTENDANCE_ROUTE];
const SETTINGS_ROUTES = [
  GENERAL_SETTINGS_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  SECURITY_SETTINGS_ROUTE,
  OFFBOARDING_SETTINGS_ROUTE,
  EXPORT_TEMPLATES_ROUTE,
  CREATE_EXPORT_TEMPLATE_ROUTE,
  EDIT_EXPORT_TEMPLATE_ROUTE,
];

/**
 * Main navigation for the signed-in account, grouped and in display order.
 * Each item lists every route that marks it active; add new pages there.
 * Items of modules the organization is not licensed for are left out.
 */
export const buildNavGroups = ({ isAdmin, accountId, routeName, modules = ALL_PLATFORM_MODULE_KEYS, chatUnread = 0 }: NavContext): NavGroup[] => {
  const item = (key: string, label: string, icon: string, to: RouteLocationRaw, routes: string[], { shortLabel = label, bottomBar = false, module = null, badge = 0 }: ItemOptions = {}): NavItem | null => {
    if (module && !modules.includes(module)) return null;
    return {
      key,
      label,
      shortLabel,
      icon,
      to,
      active: routes.includes(routeName as string),
      bottomBar,
      module,
      badge,
    };
  };

  const groups: { title: string; items: (NavItem | null)[] }[] = [
    {
      title: 'Workspace',
      items: [
        item('dashboard', 'Dashboard', 'fa-solid fa-chart-simple', { name: DASHBOARD_ROUTE }, [DASHBOARD_ROUTE], { shortLabel: 'Home', bottomBar: true }),
        item('chat', 'Chat', 'fa-regular fa-comments', { name: CHAT_ROUTE }, [CHAT_ROUTE, CHAT_CONVERSATION_ROUTE], { module: PlatformModule.chat, badge: chatUnread }),
        isAdmin
          ? item('employees', 'Employees', 'fa-solid fa-user-group', { name: EMPLOYEES_ROUTE }, EMPLOYEE_ROUTES, { bottomBar: true })
          : item('profile', 'My profile', 'fa-solid fa-user', { name: PROFILE_GENERAL_ROUTE, params: { id: accountId } }, PROFILE_ROUTES, { shortLabel: 'Profile', bottomBar: true }),
        item('attendance', 'Attendance', 'fa-solid fa-fingerprint', { name: ATTENDANCE_ROUTE }, [ATTENDANCE_ROUTE, ATTENDANCE_SETTING_ROUTE], { bottomBar: true, module: PlatformModule.attendance }),
        isAdmin
          ? item('leave', 'Leave', 'fa-regular fa-calendar-plus', { name: LEAVES_ROUTE }, [LEAVES_ROUTE, HOLIDAYS_ROUTE, LEAVE_TYPES_ROUTE], { module: PlatformModule.leave })
          : item('leave', 'Leave', 'fa-regular fa-calendar-plus', { name: PROFILE_LEAVE_ROUTE, params: { id: accountId } }, [PROFILE_LEAVE_ROUTE], { bottomBar: true, module: PlatformModule.leave }),
        isAdmin ? item('overtime', 'Overtime', 'fa-solid fa-business-time', { name: OVERTIME_ROUTE }, [OVERTIME_ROUTE], { module: PlatformModule.payroll }) : null,
      ],
    },
    {
      title: 'Manage',
      items: isAdmin
        ? [
            item('payroll', 'Payroll', 'fa-solid fa-hand-holding-dollar', { name: PAYROLL_ROUTE }, [PAYROLL_ROUTE, PAYROLL_SUMMARY_ROUTE, PAYROLL_DETAILS_ROUTE, PAYROLL_SETTINGS_ROUTE], { bottomBar: true, module: PlatformModule.payroll }),
            item('recruitment', 'Recruitment', 'fa-solid fa-person-circle-check', { name: RECRUITMENT_ROUTE, query: { tab: 'posts' } }, [RECRUITMENT_ROUTE, ADD_JOB_POST_ROUTE, EDIT_JOB_POST_ROUTE, JOB_POST_DETAILS_ROUTE, JOB_APPLICATIONS_ROUTE], { module: PlatformModule.recruitment }),
            item('projects', 'Projects', 'fa-solid fa-list-check', { name: PROJECTS_ROUTE }, [PROJECTS_ROUTE, PROJECT_DETAILS_ROUTE], { module: PlatformModule.projectManagement }),
            item('structure', 'Org. structure', 'fa-solid fa-sitemap', { name: ORGANIZATION_STRUCTURE_ROUTE }, [ORGANIZATION_STRUCTURE_ROUTE], { shortLabel: 'Structure' }),
          ]
        : [],
    },
    {
      // shared folders; every employee has it when the organization is licensed for Files
      title: 'Storage',
      items: [item('files', 'Files', 'fa-regular fa-folder-open', { name: FILES_ROUTE }, [FILES_ROUTE, FILES_FOLDER_ROUTE], { module: PlatformModule.files })],
    },
    {
      title: 'Insights',
      items: isAdmin ? [item('reports', 'Reports', 'fa-solid fa-chart-column', { name: REPORTS_ROUTE }, [REPORTS_ROUTE, REPORT_BUILDER_ROUTE])] : [],
    },
    {
      title: 'System',
      items: isAdmin ? [item('settings', 'Settings', 'fa-solid fa-gears', { name: GENERAL_SETTINGS_ROUTE }, SETTINGS_ROUTES)] : [],
    },
  ];

  return groups
    .map((group) => ({ title: group.title, items: group.items.filter((entry): entry is NavItem => entry !== null) }))
    .filter((group) => group.items.length > 0);
};
